"use client";

import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";

import {
	ChartContainer,
	ChartLegend,
	ChartLegendContent,
	ChartTooltip,
	ChartTooltipContent,
	seriesColor,
} from "./chart";
import {
	CHART_AXIS_PROPS,
	CHART_GRID_PROPS,
	type CartesianChartProps,
} from "./chart-cartesian";

export type ChartAreaProps = CartesianChartProps & {
	/**
	 * - `default` — overlaid area(s).
	 * - `stacked` — series stacked on top of each other (part-to-whole over time).
	 * - `step` — stepped interpolation for state changes.
	 */
	variant?: "default" | "stacked" | "step";
	/** Fills each area with a top-down gradient of its series colour. */
	gradient?: boolean;
	/**
	 * Grows the areas in on mount. Turn off when the chart is rasterized right
	 * after it renders, which would otherwise capture it part-drawn.
	 */
	isAnimationActive?: boolean;
	/**
	 * Category values to place X-axis ticks on, e.g. the first day of each month.
	 * Recharts picks ticks from the data by index when omitted.
	 */
	categoryTicks?: string[];
};

/**
 * Area chart built on the shared `ChartContainer` primitives. Palette-agnostic:
 * every series colour is read from `config` as `var(--color-<key>)`, so the
 * caller owns the colours (admin D33, portal tenant, or anything else).
 */
export function ChartArea({
	data,
	config,
	series,
	categoryKey,
	variant = "default",
	gradient = false,
	isAnimationActive = true,
	categoryTicks,
	className,
	valueFormatter,
	categoryFormatter,
	hideTooltip = false,
	showLegend,
}: ChartAreaProps) {
	const stacked = variant === "stacked";
	// Monotone stays smooth without overshooting, so a flat cumulative day stays flat.
	const curveType = variant === "step" ? "step" : "monotone";
	const withLegend = showLegend ?? series.length > 1;
	// React's useId emits colons, which are awkward inside a url(#…) reference.
	const gradientId = `area-gradient-${React.useId().replace(/:/g, "")}`;

	return (
		<ChartContainer config={config} className={className}>
			<AreaChart accessibilityLayer data={data}>
				{gradient && (
					<defs>
						{series.map((key) => (
							<linearGradient
								key={key}
								id={`${gradientId}-${key}`}
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop
									offset="5%"
									stopColor={seriesColor(key)}
									stopOpacity={0.8}
								/>
								<stop
									offset="95%"
									stopColor={seriesColor(key)}
									stopOpacity={0.1}
								/>
							</linearGradient>
						))}
					</defs>
				)}
				<CartesianGrid {...CHART_GRID_PROPS} />
				<XAxis
					dataKey={categoryKey}
					{...CHART_AXIS_PROPS}
					tickFormatter={categoryFormatter}
					ticks={categoryTicks}
				/>
				{!hideTooltip && (
					<ChartTooltip
						content={
							<ChartTooltipContent
								valueFormatter={valueFormatter}
								indicator={stacked ? "dot" : "line"}
							/>
						}
					/>
				)}
				{withLegend && <ChartLegend content={<ChartLegendContent />} />}
				{series.map((key) => (
					<Area
						key={key}
						dataKey={key}
						type={curveType}
						stackId={stacked ? "a" : undefined}
						stroke={seriesColor(key)}
						strokeWidth={2}
						isAnimationActive={isAnimationActive}
						fill={gradient ? `url(#${gradientId}-${key})` : seriesColor(key)}
						fillOpacity={gradient ? 1 : 0.4}
					/>
				))}
			</AreaChart>
		</ChartContainer>
	);
}
